import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { downloadXlsx } from '../utils/exportXlsx';
import ReportShell from './ReportShell';

// No-response report — tickets not yet responded to in Esri (PROGRESS ≠ COMPLETE),
// by the city (811 Status still "Not yet responded"), or either. Rows are flagged:
//   red    = neither source shows complete
//   yellow = the two sources disagree (e.g. city says complete, Esri doesn't)
// The flag is also written out as text, so it survives Excel and isn't colour-only.

const SURFACE = '#0d1b2e';
const BORDER = '#1e3a5f';
const inputStyle = { backgroundColor: SURFACE, borderColor: BORDER };

const MODES = [
  { key: 'esri', label: 'No response in Esri', hint: 'PROGRESS is not COMPLETE' },
  { key: 'city', label: 'No response by city', hint: '811 Status: city has not responded in WA 811' },
  { key: 'both', label: 'Both', hint: 'Missing a response in either source' },
];
const FLAGS = {
  both: { label: 'Not complete in either', row: 'rgba(248,113,113,0.16)', edge: '#f87171', text: '#fca5a5' },
  conflict: { label: 'Sources disagree', row: 'rgba(250,204,21,0.14)', edge: '#facc15', text: '#fde68a' },
};
const CITIES = [
  { key: 'STEILACOOM', label: 'Steilacoom' },
  { key: 'FIFE', label: 'Fife' },
  { key: 'ALGONA', label: 'Algona' },
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' }) : '—');
const isExpired = (d) => d && new Date(d) < new Date();

// Plain-language reading of which side is missing, for the Flag column / Excel.
function flagText(r) {
  if (r.flag === 'both') return r.city_done === null ? 'Not complete in Esri (city not notified)' : 'Not complete in Esri or by city';
  return r.esri_done ? 'Esri says complete, city has not responded' : 'City responded, Esri not complete';
}

export default function NoResponseReport({ onClose, embedded = false }) {
  const [mode, setMode] = useState('both');
  const [city, setCity] = useState('all');
  const [hideExpired, setHideExpired] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function run(m = mode, c = city, h = hideExpired) {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/tickets/no-response', { params: { mode: m, city: c, hideExpired: h ? '1' : '0' } });
      setReport(data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not build the report.');
    } finally {
      setLoading(false);
    }
  }

  // Re-run whenever a control changes — it's one quick query.
  useEffect(() => { run(mode, city, hideExpired); }, [mode, city, hideExpired]);

  const rows = report?.rows || [];
  const counts = useMemo(() => ({
    both: rows.filter((r) => r.flag === 'both').length,
    conflict: rows.filter((r) => r.flag === 'conflict').length,
  }), [rows]);
  const modeInfo = MODES.find((m) => m.key === mode);

  async function exportExcel() {
    await downloadXlsx({
      filename: `811-no-response-${mode}${city !== 'all' ? `-${city.toLowerCase()}` : ''}-${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: 'No Response',
      headers: ['Flag', 'Ticket #', 'City (Locate For)', 'Ticket Place', 'House #', 'Street', 'Intersecting Street', 'Called In', 'Work Begins', 'Expires', '811 Status (City)', 'PROGRESS (Esri)', 'PM1', 'Contractor', 'Ticket Link'],
      rows: rows.map((r) => [
        flagText(r), r.ticket_no, r.city, r.place || '', r.address || '', r.street || '', r.cross_street || '',
        fmtDate(r.call_date), fmtDate(r.work_begin), fmtDate(r.expires),
        r.city_status || '', r.progress || '(blank)', r.pm1 || '', r.excavator || '', r.ticket_link || '',
      ]),
    });
  }

  return (
    <ReportShell embedded={embedded} onClose={onClose} title="No Response Report" maxWidth="max-w-6xl"
      subtitle="Tickets not yet responded to — Esri PROGRESS vs. the city's response in WA 811">
        {/* Controls */}
        <div className="flex items-center gap-3 px-5 pt-4 pb-3 flex-wrap">
          <div className="flex rounded overflow-hidden border" style={{ borderColor: BORDER }} role="tablist">
            {MODES.map((m) => (
              <button
                key={m.key}
                role="tab"
                aria-selected={mode === m.key}
                onClick={() => setMode(m.key)}
                title={m.hint}
                className="text-xs px-3 py-2 font-bold uppercase tracking-wide transition"
                style={mode === m.key ? { backgroundColor: '#c9a84c', color: SURFACE } : { color: '#9ca3af' }}
              >
                {m.label}
              </button>
            ))}
          </div>
          <select value={city} onChange={(e) => setCity(e.target.value)} className="text-sm rounded px-2 py-1.5 border text-white focus:outline-none" style={inputStyle}>
            <option value="all">All cities</option>
            {CITIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none">
            <input type="checkbox" checked={hideExpired} onChange={(e) => setHideExpired(e.target.checked)} className="w-3.5 h-3.5 accent-yellow-500" />
            Hide expired tickets
          </label>
          <button
            onClick={exportExcel}
            disabled={!rows.length}
            className="ml-auto text-xs px-3 py-2 rounded border font-bold uppercase tracking-wide text-gray-300 hover:text-white transition disabled:opacity-40"
            style={{ borderColor: BORDER }}
          >
            Export to Excel
          </button>
        </div>

        <div className="overflow-y-auto px-5 pb-5 space-y-4">
          {error && <p className="text-red-400 text-sm">{error}</p>}

          {report && (
            <>
              {/* Summary + legend */}
              <div className="flex flex-wrap items-stretch gap-3">
                <div className="rounded-lg border px-3 py-2" style={{ borderColor: BORDER, backgroundColor: '#080f1a' }}>
                  <div className="text-[11px] text-gray-400 uppercase tracking-wide">{modeInfo.label}</div>
                  <div className="text-2xl font-black text-white tabular-nums">{loading ? '…' : rows.length}</div>
                </div>
                {Object.entries(FLAGS).map(([key, f]) => (
                  <div key={key} className="rounded-lg border px-3 py-2" style={{ borderColor: f.edge, backgroundColor: f.row }}>
                    <div className="text-[11px] uppercase tracking-wide" style={{ color: f.text }}>
                      {key === 'both' ? '■ Red — ' : '■ Yellow — '}{f.label}
                    </div>
                    <div className="text-2xl font-black text-white tabular-nums">{loading ? '…' : counts[key]}</div>
                  </div>
                ))}
                <p className="text-xs text-gray-500 self-center max-w-xs">
                  {modeInfo.hint}. Red: neither Esri nor the city shows it complete. Yellow: one says complete, the other doesn't.
                </p>
              </div>

              {rows.length === 0 ? (
                <p className="text-gray-500 text-sm">{loading ? 'Loading…' : 'Nothing outstanding — every ticket has a response.'}</p>
              ) : (
                <div className="rounded-lg border overflow-x-auto" style={{ borderColor: BORDER }}>
                  <table className="w-full text-xs">
                    <thead style={{ backgroundColor: '#080f1a' }}>
                      <tr>
                        {['Flag', 'Ticket #', 'City', 'Address', 'Called In', 'Work Begins', 'Expires', '811 Status (City)', 'PROGRESS (Esri)', 'PM1', 'Contractor'].map((h) => (
                          <th key={h} className="text-left px-3 py-2 font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const f = FLAGS[r.flag];
                        return (
                          <tr key={r.ticket_no} className="border-t" style={{ backgroundColor: f.row, borderColor: BORDER, boxShadow: `inset 3px 0 0 ${f.edge}` }}>
                            <td className="px-3 py-2 font-semibold whitespace-nowrap" style={{ color: f.text }}>{flagText(r)}</td>
                            <td className="px-3 py-2 font-bold whitespace-nowrap">
                              {r.ticket_link
                                ? <a href={r.ticket_link} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: '#c9a84c' }}>{r.ticket_no}</a>
                                : <span style={{ color: '#c9a84c' }}>{r.ticket_no}</span>}
                            </td>
                            <td className="px-3 py-2 text-gray-200 whitespace-nowrap">{r.city}{r.place && r.place !== r.city && <span className="text-gray-500"> (in {r.place})</span>}</td>
                            <td className="px-3 py-2 text-gray-200">{[r.address, r.street].filter(Boolean).join(' ') || '—'}{r.cross_street && <span className="text-gray-400"> @ {r.cross_street}</span>}</td>
                            <td className="px-3 py-2 text-gray-300 whitespace-nowrap">{fmtDate(r.call_date)}</td>
                            <td className="px-3 py-2 text-gray-300 whitespace-nowrap">{fmtDate(r.work_begin)}</td>
                            <td className={`px-3 py-2 whitespace-nowrap ${isExpired(r.expires) ? 'text-gray-500 line-through' : 'text-gray-300'}`}>{fmtDate(r.expires)}</td>
                            <td className={`px-3 py-2 max-w-[14rem] ${r.city_done ? 'text-white' : 'text-gray-300'}`}>{r.city_status || '—'}</td>
                            <td className={`px-3 py-2 whitespace-nowrap ${r.esri_done ? 'text-white' : 'text-gray-300'}`}>{r.progress || <span className="italic text-gray-400">blank</span>}</td>
                            <td className="px-3 py-2 text-gray-200">{r.pm1 || '—'}</td>
                            <td className="px-3 py-2 text-gray-300">{r.excavator || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
    </ReportShell>
  );
}
